const CREDENTIALS_MESSAGE =
  'The username or password is not correct. Contact your organization administrator if you need help signing in.';

const GENERIC_SIGN_IN_MESSAGE =
  'Unable to sign in right now. Please try again, or contact your organization administrator.';

const RATE_LIMIT_FALLBACK = 'Too many sign-in attempts. Please try again later.';

export function formatRetryAfter(seconds) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < 60) {
    const n = Math.max(1, Math.ceil(value));
    return n === 1 ? '1 second' : `${n} seconds`;
  }
  const minutes = Math.ceil(value / 60);
  if (minutes < 60) {
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  const hours = Math.ceil(minutes / 60);
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

function header(headers, name) {
  if (!headers) return undefined;
  const lower = name.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === lower);
  return key ? headers[key] : undefined;
}

export function parseRetryAfterSeconds(error) {
  const data = error?.response?.data || {};
  const fromBody = Number(data.retryAfter ?? data.retry_after);
  if (Number.isFinite(fromBody) && fromBody > 0) return fromBody;

  const headers = error?.response?.headers || {};
  const retryAfter = header(headers, 'retry-after');
  if (retryAfter != null && retryAfter !== '') {
    const asNum = Number(retryAfter);
    if (Number.isFinite(asNum) && asNum >= 0) return Math.max(1, Math.ceil(asNum));
    const asDate = Date.parse(retryAfter);
    if (!Number.isNaN(asDate)) {
      return Math.max(1, Math.ceil((asDate - Date.now()) / 1000));
    }
  }

  const reset = header(headers, 'ratelimit-reset');
  if (reset != null && reset !== '') {
    const asNum = Number(reset);
    if (Number.isFinite(asNum)) {
      if (asNum > 1e9) {
        return Math.max(1, Math.ceil(asNum - Date.now() / 1000));
      }
      return Math.max(1, Math.ceil(asNum));
    }
  }

  return null;
}

export function isTechnicalErrorMessage(message) {
  if (!message) return true;
  return /request failed with status code|status code \d+|network error|axios|ERR_|ECONNREFUSED|ENOTFOUND/i.test(
    String(message)
  );
}

export function shouldSkipAuthRefresh(config = {}) {
  if (config.skipAuthRedirect) return true;
  const url = String(config.url || '');
  return (
    /\/auth\/login(?:\?|$)/.test(url) ||
    /\/auth\/forgot-password(?:\?|$)/.test(url) ||
    /\/auth\/reset-password(?:\?|$)/.test(url) ||
    /\/onboarding\//.test(url)
  );
}

function rateLimitMessage(error, actionLabel) {
  const seconds = parseRetryAfterSeconds(error);
  const wait = formatRetryAfter(seconds);
  if (wait) {
    return {
      error: `Too many ${actionLabel}. Try again in ${wait}.`,
      retryAfter: seconds,
    };
  }
  return {
    error: `Too many ${actionLabel}. Please try again later.`,
    retryAfter: seconds,
  };
}

export function mapLoginError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const code =
    data.code ||
    (status === 429 ? 'RATE_LIMIT_EXCEEDED' : null) ||
    'UNKNOWN_ERROR';

  const result = {
    success: false,
    code,
    error: GENERIC_SIGN_IN_MESSAGE,
    retryAfter: null,
  };

  switch (code) {
    case 'USER_NOT_FOUND':
    case 'INVALID_PASSWORD':
      result.error = CREDENTIALS_MESSAGE;
      return result;
    case 'EMAIL_NOT_VERIFIED':
      result.error =
        'Please verify your email before signing in. Check your inbox, or contact your organization administrator.';
      return result;
    case 'NO_ACTIVE_LICENSE':
      result.error =
        'This workspace does not have an active license. Contact your organization administrator.';
      return result;
    case 'USER_NOT_AUTHORIZED':
      result.error =
        'You are not authorized to access this application. Contact your organization administrator.';
      return result;
    case 'PRODUCT_ID_REQUIRED':
      result.error =
        'This application is not configured correctly. Contact your organization administrator.';
      return result;
    case 'RATE_LIMIT_EXCEEDED': {
      const limited = rateLimitMessage(error, 'sign-in attempts');
      result.error = limited.error;
      result.retryAfter = limited.retryAfter;
      return result;
    }
    default:
      break;
  }

  if (status === 401) {
    result.code = 'INVALID_CREDENTIALS';
    result.error = CREDENTIALS_MESSAGE;
    return result;
  }

  if (status === 429) {
    result.code = 'RATE_LIMIT_EXCEEDED';
    const limited = rateLimitMessage(error, 'sign-in attempts');
    result.error = limited.error;
    result.retryAfter = limited.retryAfter;
    return result;
  }

  if (status === 403) {
    const apiMessage = data.error;
    result.error =
      apiMessage && !isTechnicalErrorMessage(apiMessage)
        ? apiMessage
        : 'You are not allowed to sign in right now. Contact your organization administrator.';
    return result;
  }

  if (!error?.response) {
    result.code = 'NETWORK_ERROR';
    result.error = 'Unable to reach Onahiri. Check your connection and try again.';
    return result;
  }

  if (isTechnicalErrorMessage(error.message) || isTechnicalErrorMessage(data.error)) {
    result.error = GENERIC_SIGN_IN_MESSAGE;
    return result;
  }

  result.error = data.error || GENERIC_SIGN_IN_MESSAGE;
  return result;
}

export function mapPublicAuthError(error, { actionLabel = 'requests' } = {}) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (status === 429 || code === 'RATE_LIMIT_EXCEEDED') {
    return rateLimitMessage(error, actionLabel).error;
  }
  if (!error?.response) {
    return 'Unable to reach Onahiri. Check your connection and try again.';
  }
  return `We could not complete that request. Try again later, or contact your organization administrator.`;
}
